import { LitElement, PropertyValues, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { EnemExercisesApiService } from "./api";
import { AuthController } from "../user-area/controllers/auth-controller";
import { MultipleChoiceQuestion } from "./multiple-choice-question";

@customElement("question-report-error")
export class QuestionReportError extends LitElement {
  private authController = new AuthController(this);
  private apiService = new EnemExercisesApiService(this.authController);

  private _handleResize;
  private _handleClickOutside;

  @property({ type: String, attribute: "question-id" })
  questionId = "";

  @state()
  reportMessage = "";

  @state()
  loading = false;

  @state()
  reportSent = false;

  @state()
  isMobile = false;

  @query(".question-report-dialog")
  private _dialog?: HTMLDialogElement;

  @query(".question-report-content-textarea")
  private _textarea?: HTMLTextAreaElement;

  private _question: MultipleChoiceQuestion | null = null;

  createRenderRoot() {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._handleResize = this.handleResize.bind(this);
    this._handleClickOutside = this.handleClickOutside.bind(this);
    window.addEventListener("resize", this._handleResize);
    document.addEventListener("click", this._handleClickOutside);

    if (document.readyState === "complete") {
      this.initialize();
    } else {
      window.addEventListener("DOMContentLoaded", this.initialize.bind(this));
    }
  }

  disconnectedCallback(): void {
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("click", this._handleClickOutside);
    super.disconnectedCallback();
  }

  protected updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    if (_changedProperties.has("questionId")) {
      this.reset();
    }
  }

  initialize() {
    this._question = document.querySelector<MultipleChoiceQuestion>(
      `multiple-choice-question[data-question-id="${this.questionId}"]`
    );
    this.handleResize();
  }

  handleResize() {
    const lastState = this.isMobile;
    this.isMobile = window.innerWidth < 1024;
    if (
      lastState !== this.isMobile &&
      this._dialog &&
      this._dialog.hasAttribute("open")
    ) {
      this.closeDialog();
      this.showDialog();
      this.focusTextarea();
    }
  }

  handleClickOutside(e: MouseEvent) {
    if (!this._dialog || !this._dialog.hasAttribute("open")) return;
    const dialogRect = this._dialog.getBoundingClientRect();
    if (
      e.clientX < dialogRect.left ||
      e.clientX > dialogRect.right ||
      e.clientY < dialogRect.top ||
      e.clientY > dialogRect.bottom
    ) {
      e.preventDefault();
      e.stopPropagation();
      requestAnimationFrame(() => {
        this.closeDialog();
      });
    }
  }

  async sendErrorReport() {
    if (
      !this._question ||
      !this._question.submitted ||
      this.reportMessage.length < 5
    ) {
      this.closeDialog();
      return;
    }

    this.loading = true;
    try {
      await this.apiService.sendQuestionErrorReport(
        this.questionId,
        this.reportMessage
      );
      this.closeDialog();
      this.loading = false;
      this.reportSent = true;
    } catch (error) {
      this.reportSent = true;
      this.loading = false;
    }
  }

  reset() {
    this.reportMessage = "";
    this.reportSent = false;
    this.loading = false;
    this.closeDialog();
  }

  handleReportButtonClick(e: MouseEvent) {
    if (this._dialog) {
      e.stopPropagation();
      if (!this._dialog.hasAttribute("open")) {
        this.showDialog();
        this.focusTextarea();
      } else {
        this.closeDialog();
      }
    }
  }

  showDialog() {
    if (!this._dialog) return;
    if (this.isMobile) (this._dialog as any).showModal();
    else (this._dialog as any).show();
  }

  closeDialog() {
    if (!this._dialog || this.loading) return;
    (this._dialog as any).close();
  }

  focusTextarea() {
    setTimeout(() => {
      this._textarea?.focus();
    }, 100);
  }

  protected render() {
    if (this.reportSent) {
      return html`<div class="question-report">
        <div class="question-report-thanks">Obrigado pelo seu feedback</div>
      </div>`;
    }

    return html`
      <div class="question-report">
        <button
          class="question-report-button"
          @click=${this.handleReportButtonClick}
        >
          Reportar Erro
        </button>
        <dialog class="question-report-dialog">
          <div class="question-report-content">
            <div class="question-report-content-header">
              <div class="question-report-content-header-title">
                Reportar Erro
              </div>
              <button
                type="button"
                aria-label="Fechar"
                title="Fechar"
                class="question-report-content-header-close"
                @click=${this.handleReportButtonClick}
              >
                <span class="icon-012-close"></span>
              </button>
            </div>
            <div class="question-report-content-title">
              Descrição do problema:
            </div>
            <textarea
              .disabled=${this.loading}
              class="question-report-content-textarea"
              placeholder="Escreva aqui para que possamos melhorar"
              @input=${(e: any) =>
                (this.reportMessage = e.target.value.substring(0, 500))}
              maxlength="500"
              autofocus
            ></textarea>
            <button
              class="question-report-content-submit"
              @click=${() => this.sendErrorReport()}
              .disabled=${this.loading || this.reportMessage.length < 5}
            >
              Enviar
            </button>
          </div>
        </dialog>
      </div>
    `;
  }
}
